import type { JSONContent } from "@tiptap/core";
import { Slice } from "@tiptap/pm/model";
import { SkillMention } from "./texteditor-utils/skill-mention-extension";
import { serializeSkillMentionToken } from "@quake/isomorphic/skill-mentions";
import { parseChatMentionChunks } from "../../lib/mentions";
import { useEditor, EditorContent, EditorEvents, type Editor, DocumentType, NodeType } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight";
import { common, createLowlight } from "lowlight";
import { cn } from "@quake/shared-ui/lib/utils";
import LiquidCommands from "./texteditor-utils/liquid/liquid-extension";
import { useRef, useEffect, createContext, useContext, JSX, useState } from "react";
import TextAlign from "@tiptap/extension-text-align";
import { EditorConfig } from "./texteditor-components/editor-config";
import { MenuBar } from "./texteditor-components/menu-bar";
import { VariableTag } from "./texteditor-utils/liquid";
import { MarkdownImage } from "./texteditor-components/markdown-image-extension";
import { MarkdownImageSerializer } from "./texteditor-components/markdown-image-serializer";
import { Table } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import { Placeholder } from "@tiptap/extensions/placeholder";
import { EmptyParagraphExtension } from "./texteditor-components/empty-paragraph-extension";
import { createRecordMentionExtension } from "./texteditor-utils/record-mention/record-mention-extension";
import { serializeRecordMentionToken } from "./texteditor-utils/record-mention/types";
import { createSlashCommandExtension, isSlashCommandActive } from "./texteditor-utils/slash-command-extension";

const lowlight = createLowlight(common);

export { type VariableTag };
export { ToolbarButton } from "./texteditor-components/image-dialog";

// EditorContext for sharing editor instance across components
interface EditorContextType {
  editor: Editor | null;
}

const EditorContext = createContext<EditorContextType>({ editor: null });

export const useCurrentEditor = () => {
  const context = useContext(EditorContext);
  return { editor: context.editor };
};

const defaultVariableTags: VariableTag[] = [
  // { tag: "first_name", label: "First Name", description: "User's first name" },
  // { tag: "last_name", label: "Last Name", description: "User's last name" },
  // { tag: "email", label: "Email Address", description: "User's email address" },
  // { tag: "company", label: "Company Name", description: "User's company or organization" },
  // { tag: "phone", label: "Phone Number", description: "User's phone number" },
  // { tag: "full_name", label: "Full Name", description: "User's complete name" },
  // { tag: "current_date", label: "Current Date", description: "Today's date" },
  // { tag: "current_time", label: "Current Time", description: "Current time" },
];

const CodeBlockStyles = () => (
  <style>{`
.tiptap{
  padding: "10px";
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.tiptap p.is-editor-empty:first-child::before {
  color: #94a3b8;
  content: attr(data-placeholder);
  float: left;
  height: 0;
  pointer-events: none;
  font-size: 0.925rem;
  font-weight: 400;
  opacity: 0.72;
}

/* List styles */
.tiptap ul,
.tiptap ol {
  padding: 0 1rem;
  margin: 1.25rem 1rem 1.25rem 0.4rem;
}

.tiptap ul li p,
.tiptap ol li p {
  margin-top: 0.25em;
  margin-bottom: 0.25em;
}


.tiptap ul {
  list-style: disc;
}

.tiptap ol {
  list-style: auto;
}

/* Heading styles */
.tiptap h1,
.tiptap h2,
.tiptap h3,
.tiptap h4,
.tiptap h5,
.tiptap h6 {
  line-height: 1.1;
  margin-top: 1.1rem;
  text-wrap: pretty;
}

.tiptap h1,
.tiptap h2 {
  margin-top: 1.1rem;
  margin-bottom: 1.1rem;
}

.tiptap h1 {
  font-size: 1.4rem;
}

.tiptap h2 {
  font-size: 1.2rem;
}

.tiptap h3 {
  font-size: 1.1rem;
}

.tiptap h4,
.tiptap h5,
.tiptap h6 {
  font-size: 1rem;
}

/* Blockquote */
.tiptap blockquote {
  border-left: 3px solid gray;
  margin: 1.5rem 0;
  padding-left: 1rem;
}

/* Horizontal Rule */
.tiptap hr {
  border: none;
  border-top: 1px solid gray;
  margin: 2rem 0;
}

/* Image placeholder */
.tiptap .image-placeholder {
  background: #f8fafc;
  border: 2px dashed #cbd5e1;
  border-radius: 0.5rem;
  padding: 2rem;
  text-align: center;
  color: #64748b;
  margin: 1rem 0;
}

.dark .tiptap .image-placeholder {
  background: #1e293b;
  border-color: #475569;
  color: #94a3b8;
}

/* Code block container */
.ProseMirror pre {
  background: #0d1117 !important;
  color: #e6edf3 !important;
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace !important;
  padding: 1rem !important;
  border-radius: 0.5rem !important;
  overflow-x: auto !important;
  margin: 1rem 0 !important;
  border: 1px solid #30363d !important;
  line-height: 1.5 !important;
  font-size: 0.875rem !important;
}

/* Code block content */
.ProseMirror pre code {
  background: transparent !important;
  color: inherit !important;
  padding: 0 !important;
  border: 0 !important;
  font-size: inherit !important;
  font-family: inherit !important;
}

/* Inline code */
.ProseMirror code {
  background: #f6f8fa !important;
  color: #24292f !important;
  padding: 0.125rem 0.375rem !important;
  border-radius: 0.25rem !important;
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace !important;
  font-size: 0.875rem !important;
  border: 1px solid #d0d7de !important;
}

/* Dark mode inline code */
.dark .ProseMirror code {
  background: #161b22 !important;
  color: #e6edf3 !important;
  border-color: #30363d !important;
}

/* Syntax highlighting */
.ProseMirror .hljs-comment,
.ProseMirror .hljs-quote {
  color: #8b949e !important;
  font-style: italic !important;
}

.ProseMirror .hljs-keyword,
.ProseMirror .hljs-selector-tag,
.ProseMirror .hljs-literal,
.ProseMirror .hljs-type {
  color: #ff7b72 !important;
}

.ProseMirror .hljs-string,
.ProseMirror .hljs-doctag {
  color: #a5d6ff !important;
}

.ProseMirror .hljs-number,
.ProseMirror .hljs-regexp,
.ProseMirror .hljs-link {
  color: #79c0ff !important;
}

.ProseMirror .hljs-function,
.ProseMirror .hljs-class,
.ProseMirror .hljs-title {
  color: #d2a8ff !important;
}

.ProseMirror .hljs-variable,
.ProseMirror .hljs-template-variable {
  color: #ffa657 !important;
}

.ProseMirror .hljs-attr,
.ProseMirror .hljs-attribute {
  color: #79c0ff !important;
}

.ProseMirror .hljs-built_in,
.ProseMirror .hljs-builtin-name {
  color: #ffa657 !important;
}

.ProseMirror .hljs-tag {
  color: #7ee787 !important;
}

.ProseMirror .hljs-name {
  color: #7ee787 !important;
}

.ProseMirror .hljs-operator {
  color: #ff7b72 !important;
}

/* Code block language indicator */
.ProseMirror pre::before {
  content: attr(data-language);
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  font-size: 0.75rem;
  color: #8b949e;
  text-transform: uppercase;
  font-weight: 500;
}

.ProseMirror pre {
  position: relative;
}

/* Scrollbar styling for code blocks */
.ProseMirror pre::-webkit-scrollbar {
  height: 8px;
}

.ProseMirror pre::-webkit-scrollbar-track {
  background: #21262d;
  border-radius: 4px;
}

.ProseMirror pre::-webkit-scrollbar-thumb {
  background: #484f58;
  border-radius: 4px;
}
.react-renderer.node-image img {
  outline: 2px solid transparent;
}
.react-renderer.node-image.ProseMirror-selectednode img {
  outline: 2px solid #3b82f6;
  outline-offset: 0px;
}

.ProseMirror pre::-webkit-scrollbar-thumb:hover {
  background: #6e7681;
}

/* Liquid tag styling */
[data-liquid-tag] {
  background-color: #f1f5f9;
  color: #1e293b;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 2px 6px;
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 0.875em;
  font-weight: 500;
  display: inline-block;
  margin: 0 1px;
  white-space: nowrap;
  transition: all 0.2s ease;
}

[data-liquid-tag]:hover {
  background-color: #e2e8f0;
  border-color: #94a3b8;
}

[data-liquid-valid="false"] {
  background-color: #fef2f2;
  color: #dc2626;
  border-color: #fca5a5;
}

[data-liquid-valid="false"]:hover {
  background-color: #fee2e2;
  border-color: #f87171;
}

/* Dark mode support */
.dark [data-liquid-tag] {
  background-color: #334155;
  color: #f1f5f9;
  border-color: #475569;
}

.dark [data-liquid-tag]:hover {
  background-color: #475569;
  border-color: #64748b;
}

.dark [data-liquid-valid="false"] {
  background-color: #451a03;
  color: #f87171;
  border-color: #7f1d1d;
}

.dark [data-liquid-valid="false"]:hover {
  background-color: #7f1d1d;
  border-color: #991b1b;
}

/* Record mention styling */
[data-record-mention] {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  background: #f8fafc;
  color: #0f172a;
  font-size: 0.75rem;
  line-height: 1rem;
  font-weight: 500;
  padding: 0.125rem 0.375rem;
  margin: 0 0.125rem;
  white-space: nowrap;
}

.dark [data-record-mention] {
  border-color: #475569;
  background: #0f172a;
  color: #e2e8f0;
}

/* Markdown Image Styles */
.markdown-image-wrapper {
  display: block;
  margin: 1rem 0;
}

.markdown-image-container {
  position: relative;
  display: inline-block;
  max-width: 100%;
}

.markdown-image-container.align-left {
  text-align: left;
}

.markdown-image-container.align-center {
  text-align: center;
  display: block;
}

.markdown-image-container.align-right {
  text-align: right;
  display: block;
}

/* Image alignment classes */
.markdown-image.align-left {
  display: block;
  margin: 0 auto 0 0;
}

.markdown-image.align-center {
  display: block;
  margin: 0 auto;
}

.markdown-image.align-right {
  display: block;
  margin: 0 0 0 auto;
}

/* Resize handle styling */
.markdown-image-container .resize-handle {
  position: absolute;
  bottom: -6px;
  right: -6px;
  width: 12px;
  height: 12px;
  background: #3b82f6;
  border: 2px solid white;
  border-radius: 50%;
  cursor: se-resize;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 10;
}

.markdown-image-container:hover .resize-handle {
  opacity: 1;
}

/* Image hover effects */
.markdown-image-container img {
  transition: all 0.2s ease;
  border-radius: 4px;
}

.markdown-image-container:hover img {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Size indicator */
.markdown-image-container .size-indicator {
  position: absolute;
  top: 4px;
  left: 4px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 3px;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace;
}

.markdown-image-container:hover .size-indicator {
  opacity: 1;
}

/* Dark mode image styles */
  .dark .markdown-image-container .size-indicator {
    background: rgba(255, 255, 255, 0.9);
    color: black;
  }

  /* Table Styles */
  .tiptap table {
    border-collapse: collapse;
    margin: 0;
    overflow: hidden;
    table-layout: fixed;
    width: 100%;
  }

  .tiptap table td,
  .tiptap table th {
    border: 1px solid #d1d5db;
    box-sizing: border-box;
    min-width: 1em;
    padding: 6px 12px;
    position: relative;
    vertical-align: top;
  }

  .dark .tiptap table td,
  .dark .tiptap table th {
    border-color: #374151;
  }

  .tiptap table th {
    background-color: #f3f4f6;
    font-weight: bold;
    text-align: left;
  }

  .dark .tiptap table th {
    background-color: #1f2937;
  }

  .tiptap table .selectedCell:after {
    background: rgba(200, 200, 255, 0.4);
    content: "";
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    pointer-events: none;
    position: absolute;
    z-index: 2;
  }

  .tiptap table .column-resize-handle {
    background-color: #3b82f6;
    bottom: -2px;
    pointer-events: none;
    position: absolute;
    right: -2px;
    top: 0;
    width: 4px;
  }

  .tiptap .tableWrapper {
    margin: 1.5rem 0;
    overflow-x: auto;
  }

  .tiptap.resize-cursor {
    cursor: ew-resize;
    cursor: col-resize;
  }

    .tippy-box {
      background-color: white !important;
      color: black !important;
      border: 1px solid rgba(0, 0, 0, 0.1) !important;
      border-radius: 12px !important;
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
      overflow: hidden !important;
    }

    .tippy-content {
      padding: 0 !important;
      background-color: transparent !important;
    }
    `}</style>
);

const defaultConfig: EditorConfig = {
  link: {
    openOnClick: false,
  },
  bold: true,
  italic: true,
  strikethrough: true,
  inlineCode: true,
  paragraph: true,
  headings: {
    h1: true,
    h2: true,
    h3: true,
    h4: false,
    h5: false,
    h6: false,
  },
  bulletList: true,
  orderedList: true,
  codeBlock: true,
  blockquote: true,
  horizontalRule: true,
  hardBreak: false,
  image: false,
  imageUrl: true,
  imageMaxWidth: 800,
  imageMaxHeight: 600,
  imageDefaultAlignment: "left",
  imageResizable: true,
  table: true,
  clearFormatting: true,
  undo: true,
  redo: true,
  enableVariableTags: true,
  enableRecordMentions: false,
  largePlainTextFallback: false,
  largePlainTextFallbackThreshold: 50_000,
  variableTags: defaultVariableTags,
  placeholder: "Start writing...",
  minHeight: "400px",
  maxWidth: "4xl",
  menu: true,
};

type BaseEditorProps = {
  id?: string;
  placeholder?: string;
  config?: Partial<EditorConfig>;
  content?: string;
  onKeyDown?: (event: KeyboardEvent) => boolean | void;
  /** Return true to prevent the editor's default paste handling. */
  onPaste?: (event: ClipboardEvent) => boolean | void;
  onEditorReady?: (editor: Editor) => void;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
  autoFocus?: boolean;
  focusKey?: unknown;
  editorClassName?: string;
};
type JsonProps = BaseEditorProps & {
  outputFormat: "json";
  onChange?: (content: DocumentType<any, any>, event: EditorEvents["update"]) => void;
};

type TextProps = BaseEditorProps & {
  outputFormat?: "markdown" | "text" | "html";
  onChange?: (content: string, event: EditorEvents["update"]) => void;
};

type TextEditorProps = TextProps | JsonProps;

function serializeEditorTextWithMentions(doc: JSONContent): string {
  const serializeNode = (node: JSONContent): string => {
    if (!node || typeof node !== "object") return "";

    if (node.type === "text") {
      return node.text || "";
    }

    if (node.type === "hardBreak") {
      return "\n";
    }

    if (node.type === "skillMention") {
      return serializeSkillMentionToken({ id: node.attrs?.id || "", label: node.attrs?.label || "" });
    }

    if (node.type === "recordMention") {
      return serializeRecordMentionToken({
        id: node.attrs?.id || "",
        source: node.attrs?.source || "",
        label: node.attrs?.label || "",
      });
    }

    const children = Array.isArray(node.content) ? node.content.map(serializeNode).join("") : "";

    if (node.type === "paragraph") {
      return `${children}\n`;
    }

    if (node.type === "doc") {
      return children;
    }

    return children;
  };

  return serializeNode(doc).replace(/\n+$/g, "");
}

function parseMentionTextToDoc(content: string, config: EditorConfig): JSONContent {
  return {
    type: "doc",
    content: content
      .replace(/\r\n?/g, "\n")
      .split("\n")
      .map((line) => ({
        type: "paragraph",
        content: parseChatMentionChunks(line).flatMap((chunk): JSONContent[] => {
          if (chunk.type === "skill") {
            return config.slashCommandOptions
              ? [{ type: "skillMention", attrs: chunk.value }]
              : [{ type: "text", text: serializeSkillMentionToken(chunk.value) }];
          }
          if (chunk.type === "mention") {
            return config.enableRecordMentions
              ? [{ type: "recordMention", attrs: { ...chunk.value, icon: null } }]
              : [{ type: "text", text: serializeRecordMentionToken(chunk.value) }];
          }
          return chunk.value ? [{ type: "text", text: chunk.value }] : [];
        }),
      })),
  };
}

export function TextEditor(props: JsonProps): JSX.Element | null;
export function TextEditor(props: TextProps): JSX.Element | null;
export function TextEditor({
  id,
  placeholder,
  config: userConfig = {},
  content = "",
  onChange,
  onKeyDown,
  onPaste,
  onEditorReady,
  disabled = false,
  className = "",
  outputFormat = "markdown",
  ariaLabel,
  autoFocus = false,
  focusKey,
  editorClassName = "",
}: TextEditorProps) {
  const config = {
    ...defaultConfig,
    ...userConfig,
    placeholder: placeholder ?? userConfig.placeholder ?? defaultConfig.placeholder,
  };
  const hasInlineMentions = Boolean(config.enableRecordMentions || config.slashCommandOptions);
  const suggestionRef = useRef(config.variableTags || defaultVariableTags);
  const slashCommandOptionsRef = useRef(config.slashCommandOptions);
  slashCommandOptionsRef.current = config.slashCommandOptions;
  const lastEmittedTextRef = useRef<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const plainTextFallbackThreshold = config.largePlainTextFallbackThreshold ?? 50_000;
  const [forcePlainTextFallback, setForcePlainTextFallback] = useState(
    () =>
      config.largePlainTextFallback === true &&
      outputFormat === "text" &&
      typeof content === "string" &&
      content.length > plainTextFallbackThreshold,
  );
  const usePlainTextFallback =
    config.largePlainTextFallback === true &&
    outputFormat === "text" &&
    (forcePlainTextFallback || (typeof content === "string" && content.length > plainTextFallbackThreshold));
  const handleTextChange = onChange as ((content: string, event: EditorEvents["update"]) => void) | undefined;

  // Build extensions based on config
  const extensions: any[] = [
    StarterKit.configure({
      // Disable code block from StarterKit if we're using CodeBlockLowlight
      codeBlock: false,
      bulletList: config.bulletList
        ? {
            keepMarks: true,
            keepAttributes: false,
          }
        : false,
      orderedList: config.orderedList
        ? {
            keepMarks: true,
            keepAttributes: false,
          }
        : false,
      bold: config.bold ? {} : false,
      italic: config.italic ? {} : false,
      strike: config.strikethrough ? {} : false,
      code: config.inlineCode ? {} : false,
      blockquote: config.blockquote ? {} : false,
      horizontalRule: config.horizontalRule ? {} : false,
      hardBreak: config.hardBreak ? {} : false,
      paragraph: false,
      heading: Object.values(config.headings || {}).some(Boolean)
        ? {
            levels: Object.entries(config.headings || {})
              .filter(([, enabled]) => enabled)
              .map(([level]) => Number.parseInt(level.replace("h", "")) as 1 | 2 | 3 | 4 | 5 | 6),
          }
        : false,
      link: config.link,
    }),
    Table.configure({
      resizable: true,
    }),
    TableRow,
    TableHeader,
    TableCell,
    CodeBlockLowlight.configure({
      lowlight,
      defaultLanguage: null,
    }),
    LiquidCommands.configure({
      suggestion: {
        items: ({ query }) => {
          return suggestionRef.current
            .filter((tag) => tag.tag.toLowerCase().includes(query.toLowerCase()))
            .map((tag) => ({
              label: tag.label,
              tag: tag.tag,
              description: tag.description,
            }));
        },
      },
    }),
    TextAlign.configure({ types: ["heading", "paragraph"] }),
    MarkdownImageSerializer,
    Placeholder.configure({
      placeholder: config.placeholder || "Start writing...",
      emptyEditorClass: "is-editor-empty",
    }),
    // Always include MarkdownImage so image URLs in markdown always render,
    // regardless of whether the image toolbar button is enabled.
    MarkdownImage.configure({
      HTMLAttributes: {
        class: "markdown-image",
      },
    }),
    EmptyParagraphExtension,
  ];
  if (config.enableRecordMentions && config.recordMentionOptions?.items) {
    extensions.push(
      createRecordMentionExtension({
        items: ({ query }) => config.recordMentionOptions?.items({ query }) ?? [],
      }),
    );
  }
  if (config.slashCommandOptions) {
    extensions.push(
      SkillMention,
      createSlashCommandExtension(() => slashCommandOptionsRef.current),
    );
  }

  useEffect(() => {
    if (config.variableTags) {
      suggestionRef.current = config.variableTags;
    } else {
      suggestionRef.current = [];
    }
  }, [config.variableTags]);

  const shouldForcePlainTextPaste = config.forcePlainTextPaste === true;

  const editor = useEditor({
    extensions: extensions,
    content: usePlainTextFallback
      ? ""
      : hasInlineMentions && outputFormat === "text"
        ? parseMentionTextToDoc(content || "", config)
        : content?.replace(/<br>|&nbsp;/g, "\u00A0"),
    contentType:
      hasInlineMentions && outputFormat === "text" ? "json" : outputFormat === "markdown" ? "markdown" : "html",
    onUpdate: ({ editor, transaction, appendedTransactions }) => {
      let output =
        outputFormat !== "json"
          ? ""
          : ({} as Omit<NodeType<"doc", any, never, any>, "content" | "marks"> & { content: any });
      if (outputFormat === "markdown") {
        output = editor.getMarkdown();
      } else if (outputFormat === "text") {
        output = hasInlineMentions ? serializeEditorTextWithMentions(editor.getJSON()) : editor.getText();
      } else if (outputFormat === "html") {
        output = editor.getHTML();
      } else if (outputFormat === "json") {
        output = editor.getJSON();
      }
      if (typeof output === "string") {
        lastEmittedTextRef.current = output;
      }
      onChange?.(output as any, { editor, transaction, appendedTransactions });
    },
    editorProps: {
      attributes: {
        ...(id ? { id } : {}),
        class: cn(
          `prose prose-sm sm:prose-base lg:prose-lg xl:prose-2xl mx-auto focus:outline-none p-6 whitespace-pre-wrap`,
          editorClassName,
        ),
        style: `min-height: ${config.minHeight || "400px"}; white-space: pre-wrap; overflow-wrap: anywhere; word-break: break-word;`,
        placeholder: config.placeholder || "Start writing...",
        ...(ariaLabel ? { "aria-label": ariaLabel } : {}),
        role: "textbox",
        "aria-multiline": "true",
      },
      handleKeyDown: (_view, event) => {
        if (event.defaultPrevented) return true;
        // Let the suggestion plugin consume selection keys before the caller's Enter-to-send handler.
        if (isSlashCommandActive(_view.state)) return false;
        const result = onKeyDown?.(event);
        if (result === true) return true;
        return false;
      },
      handleDrop: (view, event) => {
        const files = Array.from(event.dataTransfer?.files || []);
        const imageFiles = files.filter((file) => file.type.startsWith("image/"));

        if (!config.image) {
          if (imageFiles.length > 0) {
            event.preventDefault();
            return true;
          }
          return false;
        }

        if (imageFiles.length === 0) return false;

        event.preventDefault();

        const { schema } = view.state;
        const coordinates = view.posAtCoords({ left: event.clientX, top: event.clientY });

        imageFiles.forEach(async (file) => {
          try {
            let imageSrc: string;

            if (config.onImageUpload) {
              imageSrc = await config.onImageUpload(file);
            } else {
              imageSrc = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsDataURL(file);
              });
            }

            if (!schema.nodes.image) {
              console.error("Image node type not available");
              return;
            }

            const node = schema.nodes.image.create({
              src: imageSrc,
              alt: file.name,
              alignment: config.imageDefaultAlignment || "left",
            });

            const transaction = view.state.tr.insert(coordinates?.pos || 0, node);
            view.dispatch(transaction);
          } catch (error) {
            console.error("Error uploading dropped image:", error);
          }
        });

        return true;
      },
      handlePaste: (view, event) => {
        const pasteHandled = onPaste?.(event);
        if (pasteHandled === true || event.defaultPrevented) {
          return true;
        }

        const files = Array.from(event.clipboardData?.files || []);
        const imageFiles = files.filter((file) => file.type.startsWith("image/"));

        if (config.image && imageFiles.length > 0) {
          event.preventDefault();

          const { schema } = view.state;

          imageFiles.forEach(async (file) => {
            try {
              let imageSrc: string;

              if (config.onImageUpload) {
                imageSrc = await config.onImageUpload(file);
              } else {
                imageSrc = await new Promise<string>((resolve, reject) => {
                  const reader = new FileReader();
                  reader.onload = () => resolve(reader.result as string);
                  reader.onerror = reject;
                  reader.readAsDataURL(file);
                });
              }

              if (!schema.nodes.image) {
                console.error("Image node type not available");
                return;
              }

              const node = schema.nodes.image.create({
                src: imageSrc,
                alt: file.name,
                alignment: config.imageDefaultAlignment || "left",
              });

              const transaction = view.state.tr.replaceSelectionWith(node);
              view.dispatch(transaction);
            } catch (error) {
              console.error("Error uploading pasted image:", error);
            }
          });

          return true;
        }

        if (imageFiles.length > 0) {
          event.preventDefault();
          const text = event.clipboardData?.getData("text/plain") ?? "";

          if (!shouldForcePlainTextPaste || !text) {
            return true;
          }

          const normalizedText = text.replace(/\r\n?/g, "\n");
          const transaction =
            hasInlineMentions && outputFormat === "text"
              ? view.state.tr
                  .replaceSelection(
                    Slice.maxOpen(
                      view.state.schema.nodeFromJSON(parseMentionTextToDoc(normalizedText, config)).content,
                    ),
                  )
                  .scrollIntoView()
              : view.state.tr.insertText(normalizedText).scrollIntoView();
          transaction.setMeta("paste", true);
          transaction.setMeta("uiEvent", "paste");
          view.dispatch(transaction);

          return true;
        }

        if (!shouldForcePlainTextPaste) {
          return false;
        }

        const text = event.clipboardData?.getData("text/plain") ?? "";

        if (!text) {
          return false;
        }

        event.preventDefault();

        const normalizedText = text.replace(/\r\n?/g, "\n");
        const shouldUsePlainTextFallback =
          config.largePlainTextFallback === true &&
          outputFormat === "text" &&
          normalizedText.length + view.state.doc.content.size > plainTextFallbackThreshold;

        if (shouldUsePlainTextFallback) {
          event.preventDefault();
          const currentText =
            hasInlineMentions && outputFormat === "text"
              ? serializeEditorTextWithMentions(view.state.doc.toJSON())
              : view.state.doc.textBetween(0, view.state.doc.content.size, "\n", "\n");
          const nextText = currentText ? `${currentText}${normalizedText}` : normalizedText;
          lastEmittedTextRef.current = nextText;
          setForcePlainTextFallback(true);
          handleTextChange?.(nextText, {
            editor: null as unknown as Editor,
            transaction: view.state.tr,
            appendedTransactions: [],
          } as EditorEvents["update"]);
          return true;
        }

        const transaction =
          hasInlineMentions && outputFormat === "text"
            ? view.state.tr
                .replaceSelection(
                  Slice.maxOpen(view.state.schema.nodeFromJSON(parseMentionTextToDoc(normalizedText, config)).content),
                )
                .scrollIntoView()
            : view.state.tr.insertText(normalizedText).scrollIntoView();
        transaction.setMeta("paste", true);
        transaction.setMeta("uiEvent", "paste");
        view.dispatch(transaction);

        return true;
      },
    },
  });

  useEffect(() => {
    if (editor) onEditorReady?.(editor);
  }, [editor, onEditorReady]);

  useEffect(() => {
    if (editor) {
      if (usePlainTextFallback) {
        return;
      }

      if (typeof content === "string" && content === lastEmittedTextRef.current) {
        return;
      }

      const currentValue =
        hasInlineMentions && outputFormat === "text"
          ? serializeEditorTextWithMentions(editor.getJSON())
          : editor.getMarkdown();
      if (content !== currentValue) {
        if (hasInlineMentions && outputFormat === "text") {
          editor.commands.setContent(parseMentionTextToDoc(content || "", config), {
            contentType: "json",
          });
        } else if (outputFormat === "markdown") {
          editor.commands.setContent(content?.replace(/<br>|&nbsp;/g, "\u00A0"), {
            contentType: "markdown",
          });
        } else if (outputFormat === "html") {
          editor.commands.setContent(content, {
            contentType: "html",
          });
        } else {
          editor.commands.setContent(content || "");
        }
      }
    }
  }, [hasInlineMentions, content, editor, outputFormat, usePlainTextFallback]);

  useEffect(() => {
    if (editor) editor.setEditable(!disabled);
  }, [editor, disabled]);

  useEffect(() => {
    if (!editor || disabled || (!autoFocus && focusKey === undefined)) return;

    if (usePlainTextFallback) {
      const frame = window.requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });

      return () => window.cancelAnimationFrame(frame);
    }

    const frame = window.requestAnimationFrame(() => {
      editor.commands.focus("end");
    });

    return () => window.cancelAnimationFrame(frame);
  }, [autoFocus, disabled, editor, focusKey, usePlainTextFallback]);

  useEffect(() => {
    if (!config.largePlainTextFallback || outputFormat !== "text" || typeof content !== "string") return;

    if (content.length > plainTextFallbackThreshold) {
      setForcePlainTextFallback(true);
      return;
    }

    if (forcePlainTextFallback && content.length === 0) {
      setForcePlainTextFallback(false);
    }
  }, [config.largePlainTextFallback, content, forcePlainTextFallback, outputFormat, plainTextFallbackThreshold]);

  if (!editor) {
    return null;
  }

  if (usePlainTextFallback && outputFormat === "text") {
    return (
      <div className={cn("w-full mx-auto", className)}>
        <textarea
          id={id}
          ref={textareaRef}
          aria-label={ariaLabel}
          value={content || ""}
          disabled={disabled}
          placeholder={config.placeholder || "Start writing..."}
          onChange={(event) => {
            lastEmittedTextRef.current = event.target.value;
            handleTextChange?.(event.target.value, {
              editor,
              transaction: editor.state.tr,
              appendedTransactions: [],
            });
          }}
          onKeyDown={(event) => {
            if (event.defaultPrevented) return;
            const result = onKeyDown?.(event.nativeEvent);
            if (result === true) {
              event.preventDefault();
            }
          }}
          onPaste={(event) => {
            const pasteHandled = onPaste?.(event.nativeEvent);
            if (pasteHandled === true) {
              event.preventDefault();
            }
          }}
          className={cn(
            "w-full resize-none bg-transparent p-1 pt-1.5 text-sm focus:outline-none disabled:cursor-not-allowed disabled:opacity-50",
            "whitespace-pre-wrap break-words [overflow-wrap:anywhere]",
            editorClassName,
          )}
          style={{
            minHeight: config.minHeight || "400px",
          }}
        />
      </div>
    );
  }

  return (
    <EditorContext.Provider value={{ editor }}>
      <div className={cn("w-full mx-auto", className)}>
        <div
          className={cn(
            "overflow-auto",
            config.compact
              ? "border-0 shadow-none bg-transparent rounded-none"
              : "border rounded-lg shadow-sm bg-background",
          )}
        >
          <CodeBlockStyles />
          {config.menu && <MenuBar config={config} />}
          <EditorContent editor={editor} />
        </div>
      </div>
    </EditorContext.Provider>
  );
}
